/**
 * SiteJsonLd — schema.org Organization + WebSite structured data.
 *
 * Injected ONCE in App.js so Google associates a stable brand identity with
 * the domain (Organization) and knows the site supports a search action
 * (WebSite with SearchAction) — enables the "sitelinks search box" on Google
 * results pages.
 *
 * Refs :
 *   - https://developers.google.com/search/docs/appearance/site-names
 *   - https://developers.google.com/search/docs/appearance/structured-data/sitelinks-searchbox
 *   - https://developers.google.com/search/docs/appearance/structured-data/logo
 */
import { useEffect } from 'react';

const JSON_LD_ID = 'realty237-site-jsonld';

const SiteJsonLd = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const origin = window.location.origin;

    const graph = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${origin}/#organization`,
          name: 'Realty237',
          alternateName: 'Realty 237',
          url: origin,
          logo: {
            '@type': 'ImageObject',
            url: `${origin}/logo512.png`,
            width: 512,
            height: 512,
          },
          description:
            "Realty237 — plateforme immobilière camerounaise. Maisons, appartements, terrains, bureaux et salles de fête à louer ou à vendre partout au Cameroun.",
          areaServed: {
            '@type': 'Country',
            name: 'Cameroun',
            identifier: 'CM',
          },
          knowsLanguage: ['fr-FR', 'en-US'],
          // SEO cahier des charges §1.2 — sameAs vers profil Google Business
          // + réseaux sociaux, pour aider Google à consolider l'identité.
          // La variable REACT_APP_GBP_URL sera renseignée en production après
          // création du Google Business Profile ; placeholder par défaut =
          // recherche Google standard sur la marque.
          sameAs: [
            process.env.REACT_APP_GBP_URL || 'https://www.google.com/search?q=Realty237+Cameroun',
            'https://www.facebook.com/realty237',
            'https://www.instagram.com/realty237',
            'https://www.linkedin.com/company/realty237',
            'https://www.youtube.com/@realty237',
            'https://twitter.com/realty237',
          ].filter(Boolean),
          contactPoint: [
            {
              '@type': 'ContactPoint',
              telephone: '+237-6-99-11-22-33',
              contactType: 'customer service',
              areaServed: 'CM',
              availableLanguage: ['French', 'English'],
            },
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${origin}/#website`,
          url: origin,
          name: 'Realty237',
          inLanguage: 'fr-FR',
          publisher: { '@id': `${origin}/#organization` },
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${origin}/properties?search={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        },
      ],
    };

    let el = document.getElementById(JSON_LD_ID);
    if (!el) {
      el = document.createElement('script');
      el.id = JSON_LD_ID;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(graph);

    // Global site JSON-LD — intentionally NOT removed on unmount. It must
    // persist across every SPA route so Google always sees it regardless of
    // the entry point.
    return undefined;
  }, []);

  return null;
};

export default SiteJsonLd;
